import {
  Auftrag1_10ZeileTypes,
  Befehl1FormData,
  Befehl1FormSchema,
  DEFAULT_AUFTRAG1_10_ZEILE
} from './schema'
import { Befehl1 } from '../../../models'
import { Betriebsstellenart } from '../../../types'
import {
  BefehlAuftrag_1_10Dto,
  BefehlAuftrag_1_25Dto,
  BefehlAuftrag_1_95Dto
} from '../../../dtos'
import {
  Befehl1DataDto,
  Feld_1_11_1Dto,
  Feld_1_11_2Dto,
  Feld_1_12_1Dto,
  Feld_1_12_2Dto,
  Feld_1_13_1Dto,
  Feld_1_13_2Dto
} from '../../../dtos/befehl-1-dto'
import {
  createFreitextAuftrag,
  createKmField,
  createSignalWithZugmeldestelleField,
  createSimpleAuftrag,
  findAuftragByKennung,
  hasAuftrag
} from '../converter-utils'

// Helper functions
const parseZeileFromKennung = (kennung: string) => {
  const parts = kennung.split('.')
  if (parts.length < 3) return null

  const zeileNum = Number.parseInt(parts[1]?.substring(1) || '0')
  const typSuffix = parts[2]

  return {
    index: zeileNum - 1,
    type: determineTypeFromSuffix(typSuffix)
  }
}

function determineTypeFromSuffix(typSuffix: string) {
  if (typSuffix === '1') return 'km'
  if (typSuffix === '2') return 'signal'
  return null
}

const hasKmData = (werte: unknown): werte is { km: string } => {
  return werte != null && typeof werte === 'object' && 'km' in werte
}

const hasSignalData = (
  werte: unknown
): werte is {
  signal: string
  zugmeldestelle: { art?: string; name?: string }
} => {
  return (
    werte != null &&
    typeof werte === 'object' &&
    'signal' in werte &&
    'zugmeldestelle' in werte
  )
}

const createKmZeile = (km: string) => ({
  type: Auftrag1_10ZeileTypes.KM,
  km,
  signal: '',
  zugmeldestelle: {
    art: '' as const,
    name: ''
  }
})

const createSignalZeile = (werte: {
  signal: string
  zugmeldestelle: { art?: string; name?: string }
}) => ({
  type: Auftrag1_10ZeileTypes.SIGNAL_ZUGMELDESTELLE,
  km: '',
  signal: werte.signal,
  zugmeldestelle: {
    art: (werte.zugmeldestelle?.art as Betriebsstellenart) ?? '',
    name: (werte.zugmeldestelle?.name as string) ?? ''
  }
})

export const convertModelToBefehl1FormData = (
  befehl?: Befehl1
): Befehl1FormData => {
  const formData = Befehl1FormSchema.parse({})

  if (!befehl?.auftraege) {
    return formData
  }

  handelAuftrag1_10ModelToFormData(befehl, formData)

  if (hasAuftrag(befehl.auftraege, '1.25')) {
    formData.auftrag_1_25.enabled = true
  }

  const auftrag195 = findAuftragByKennung(befehl.auftraege, '1.95')
  const firstField = auftrag195?.felder?.[0]

  if (auftrag195) {
    formData.auftrag_1_95.enabled = true
    if (firstField?.werte?.freitext) {
      formData.auftrag_1_95.freitext = firstField.werte.freitext
    }
  }

  return formData
}

function handelAuftrag1_10ModelToFormData(
  befehl: Befehl1,
  formData: Befehl1FormData
) {
  // enabled not changeable by user
  const auftrag110 = findAuftragByKennung(
    befehl.auftraege,
    '1.10'
  ) as BefehlAuftrag_1_10Dto
  if (auftrag110) {
    formData.auftrag_1_10.enabled = true
    const zeilen: Array<typeof DEFAULT_AUFTRAG1_10_ZEILE | undefined> = [
      undefined,
      undefined,
      undefined
    ]

    handleZeilenAuftrag1_10ModelToFormData(auftrag110.felder, zeilen)

    const processedZeilen = zeilen.filter((zeile) => zeile !== undefined)

    formData.auftrag_1_10.zeilen =
      processedZeilen.length > 0 ? processedZeilen : []
  }
}

function handleZeilenAuftrag1_10ModelToFormData(
  felder: (
    | Feld_1_11_1Dto
    | Feld_1_11_2Dto
    | Feld_1_12_1Dto
    | Feld_1_12_2Dto
    | Feld_1_13_1Dto
    | Feld_1_13_2Dto
  )[],
  zeilen: Array<typeof DEFAULT_AUFTRAG1_10_ZEILE | undefined>
) {
  for (const field of felder) {
    if (!field?.kennung || !field?.werte) continue

    const zeile = parseZeileFromKennung(field.kennung)
    if (!zeile) continue

    const { index, type } = zeile
    if (index < 0 || index >= 3) continue

    if (type === 'km' && hasKmData(field.werte)) {
      zeilen[index] = createKmZeile(field.werte.km)
    } else if (type === 'signal' && hasSignalData(field.werte)) {
      zeilen[index] = createSignalZeile(field.werte)
    }
  }
}

export const convertBefehl1FormDataToModel = (
  formData: Befehl1FormData,
  frontendId: string
): Befehl1 => {
  const auftraege: Befehl1DataDto = []

  if (formData.auftrag_1_10?.enabled && formData.auftrag_1_10.zeilen) {
    const auftrag110: BefehlAuftrag_1_10Dto = {
      kennung: '1.10',
      felder: []
    }

    handelAuftrag1_10FormDataToModel(auftrag110, formData)

    auftraege.push(auftrag110)
  }

  if (formData.auftrag_1_25?.enabled) {
    auftraege.push(createSimpleAuftrag('1.25', true) as BefehlAuftrag_1_25Dto)
  }

  if (formData.auftrag_1_95?.enabled) {
    auftraege.push(
      createFreitextAuftrag(
        '1',
        true,
        formData.auftrag_1_95?.freitext || ''
      ) as BefehlAuftrag_1_95Dto
    )
  }

  return {
    kennung: '1',
    frontendId,
    auftraege
  }
}

function handelAuftrag1_10FormDataToModel(
  auftrag110: BefehlAuftrag_1_10Dto,
  formData: Befehl1FormData
) {
  for (const [index, zeile] of formData.auftrag_1_10.zeilen.entries()) {
    if (zeile) {
      const zeileNum = index + 1
      const kennungMapping = {
        1: { km: '1.11.1', signal: '1.11.2' },
        2: { km: '1.12.1', signal: '1.12.2' },
        3: { km: '1.13.1', signal: '1.13.2' }
      }
      const kennungen = kennungMapping[zeileNum as 1 | 2 | 3]

      if (kennungen) {
        if (zeile.type === Auftrag1_10ZeileTypes.KM) {
          auftrag110.felder.push(
            createKmField(kennungen.km, zeile.km || '') as
              | Feld_1_11_1Dto
              | Feld_1_12_1Dto
              | Feld_1_13_1Dto
          )
        } else if (zeile.type === Auftrag1_10ZeileTypes.SIGNAL_ZUGMELDESTELLE) {
          auftrag110.felder.push(
            createSignalWithZugmeldestelleField(
              kennungen.signal,
              zeile.signal || '',
              zeile.zugmeldestelle?.art ?? '',
              zeile.zugmeldestelle?.name ?? ''
            ) as Feld_1_11_2Dto | Feld_1_12_2Dto | Feld_1_13_2Dto
          )
        }
      }
    }
  }
}
